import React from 'react';
import { Select } from '../../../../shared/components/Select';
import { ModuleDef, ModuleState, ModuleSourceRow, sumPipeCounts } from './moduleDefs';
import { ModuleSourceTable } from './ModuleSourceTable';
import { ModulePriceBreakdownPanel } from './ModulePriceBreakdownPanel';
import { ModuleBreakdown } from '../../utils/pricingSheetData';
import { TIER_STANDARD, TIER_AI } from './ndtCostModel';
import { NDTRatesDrawer } from './NDTRatesDrawer';

/**
 * NDT detail pane. The deal facts live on the page: the inspection tier and the
 * vendor onboarding rows. Like Material Traceability, the total inspection
 * volume is summed from the per-vendor inspection counts. The per-inspection and
 * vendor-onboarding rates live in the Edit cost assumptions drawer, and the
 * full calculation shows in the price breakdown panel.
 */

interface Props {
  def: ModuleDef;
  module: ModuleState;
  onChange: (patch: Partial<ModuleState>) => void;
  breakdown?: ModuleBreakdown | null;
  ndtConfig?: Record<string, number>;
  nreConfig?: Record<string, number>;
}

const TIER_OPTIONS = [
  { value: TIER_STANDARD, label: 'Standard' },
  { value: TIER_AI, label: 'NDT + AI' },
];

export const NDTPane: React.FC<Props> = ({ def, module, onChange, breakdown, ndtConfig, nreConfig }) => {
  const [ratesOpen, setRatesOpen] = React.useState(false);
  const overrides = module.overrides ?? {};
  const tier = (module.tier as string) ?? TIER_STANDARD;
  const overrideCount = Object.keys(overrides).length;

  // NDT derives its total inspection volume from the per-vendor inspection
  // counts, the same way Material Traceability sums per-mill pipe counts.
  const sources = module.sources ?? [];
  const totalInspections = sumPipeCounts(sources) ?? 0;

  const handleSourcesChange = (next: ModuleSourceRow[]) => {
    onChange({ sources: next, volume: sumPipeCounts(next) ?? 0 });
  };

  const setOverride = (key: string, value: number | undefined) => {
    const next = { ...overrides };
    if (value === undefined || Number.isNaN(value)) delete next[key];
    else next[key] = value;
    onChange({ overrides: next });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-heading font-semibold text-blueprint-blue">NDT</h2>
          <p className="mt-1 text-sm text-heritage-grey">Per-inspection charge plus vendor onboarding.</p>
        </div>
        <div className="flex items-end gap-3">
          <button
            type="button"
            onClick={() => setRatesOpen(true)}
            className="rounded-lg border border-[#E2E5EA] px-3 py-2 text-sm font-medium text-text-grey hover:bg-page-bg"
          >
            Edit cost assumptions
            {overrideCount > 0 && (
              <span className="ml-1.5 rounded-full bg-primary px-1.5 py-0.5 text-xs text-white">{overrideCount}</span>
            )}
          </button>
          <div className="w-44">
            <Select
              label="Inspection tier"
              tooltip="Standard uses the standard rate. NDT + AI prices the AI-assisted service at the AI rate."
              value={tier}
              options={TIER_OPTIONS}
              onChange={(value) => onChange({ tier: value })}
            />
          </div>
        </div>
      </div>

      <p className="text-sm font-medium text-text-grey">
        Total inspections: {totalInspections.toLocaleString()}
        <span className="ml-2 font-normal text-heritage-grey">summed from the vendor rows below.</span>
      </p>

      <ModuleSourceTable def={def} sources={sources} onChange={handleSourcesChange} />

      <ModulePriceBreakdownPanel productId="ndt" breakdown={breakdown} />

      <NDTRatesDrawer
        open={ratesOpen}
        onClose={() => setRatesOpen(false)}
        config={ndtConfig}
        nreConfig={nreConfig}
        overrides={overrides}
        onOverrideChange={setOverride}
        onResetAll={() => onChange({ overrides: {} })}
        discountDefault={
          (breakdown?.details as { discountPercentage?: number } | undefined)?.discountPercentage ?? 0
        }
      />
    </div>
  );
};
